import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import LandingNav from '../components/landing/HomeNav'
import HeroSection from '../components/landing/HeroSection'
import AgentsSection from '../components/landing/AgentsSection'
import WorkflowSection from '../components/landing/WorkflowSection'
import ShowcaseSection from '../components/landing/ShowcaseSection'
import Footer from '../components/landing/Footer'
import FloatingInputBar from '../components/landing/FloatingInputBar'
import { useCreateProject } from '../api/hooks'
import { DEFAULT_STYLE, type VisualStyle } from '../constants/styles'
import type { ProjectFormat } from '../api/types'
import type { ProductionTarget } from '../components/landing/hero-content'

export default function LandingPage() {
  const navigate = useNavigate()
  const createProject = useCreateProject()
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [currentStyle, setCurrentStyle] = useState<VisualStyle>(DEFAULT_STYLE)
  const [currentProductionTarget, setCurrentProductionTarget] = useState<ProductionTarget>('webtoon')

  const handleGenerate = async (
    prompt: string,
    style: VisualStyle,
    productionTarget: ProductionTarget,
  ) => {
    if (isCreating) return

    setIsCreating(true)
    setError(null)

    try {
      const projectFormat: ProjectFormat =
        productionTarget === 'video' ? 'short_drama' : 'webtoon'
      const project = await createProject.mutateAsync({ prompt, format: projectFormat })

      navigate(`/v2/project/${project.id}/workspace`, {
        state: { initialPrompt: prompt, style, productionTarget },
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create project')
      setIsCreating(false)
    }
  }

  const handleStyleChange = useCallback((style: VisualStyle, productionTarget: ProductionTarget) => {
    setCurrentStyle(style)
    setCurrentProductionTarget(productionTarget)
  }, [])

  const handleFloatingSubmit = (prompt: string) => {
    handleGenerate(prompt, currentStyle, currentProductionTarget)
  }

  const handleRecreate = (prompt: string) => {
    handleGenerate(prompt, currentStyle, currentProductionTarget)
  }

  return (
    <div data-page="landing" className="landing-page min-h-screen text-white overflow-x-clip relative bg-black">
      <div className="background-glow" />
      <LandingNav />
      <HeroSection onGenerate={handleGenerate} isCreating={isCreating} onStyleChange={handleStyleChange} />
      <AgentsSection />
      <WorkflowSection />
      <ShowcaseSection onRecreate={handleRecreate} />
      <Footer />
      <FloatingInputBar onSubmit={handleFloatingSubmit} isGenerating={isCreating} />

      {error && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-red-500/90 text-white px-4 py-2 rounded-lg shadow z-50">
          {error}
          <button onClick={() => setError(null)} className="ml-2 opacity-70 hover:opacity-100">×</button>
        </div>
      )}
    </div>
  )
}
